
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { Notice } from '../types';

// EmailJS 전역 선언
declare const emailjs: any;

const BrandStoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [expandedNotice, setExpandedNotice] = useState<Notice | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    studentName: '',
    phone: '',
    ageGender: '',
    region: '',
    time: ''
  });

  // 실시간 공지사항 가져오기 (최신 4개)
  useEffect(() => {
    const q = query(collection(db, "notices"), orderBy("createdAt", "desc"), limit(4));
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notice));
      setNotices(data);
    });
    return () => unsubscribe();
  }, []);

  // 스크롤 시 페이드인 효과를 위한 인터섹션 옵저버 설정
  useEffect(() => {
    const observerOptions = { threshold: 0.1 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [notices]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSending) return;

    setIsSending(true);
    
    /**
     * [EmailJS 연동 정보]
     * 아래 값들을 실제 EmailJS에서 발급받은 ID로 교체하세요.
     */
    const SERVICE_ID = "service_a1fnezj"; // 예: service_xxxx
    const TEMPLATE_ID = "template_g0a7o3b"; // 예: template_xxxx
    const PUBLIC_KEY = "qUG9UFiGfm3jsVehx"; // 예: user_xxxx...

    const templateParams = {
      studentName: formData.studentName,
      phone: formData.phone,
      ageGender: formData.ageGender,
      region: formData.region,
      time: formData.time,
      date: new Date().toLocaleString(),
      to_email: 'seven_iris@naver.com'
    };

    try {
      if (typeof emailjs !== 'undefined') {
        // EmailJS 공식 가이드에 따른 전송 방식
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
        if (window.customAlert) await window.customAlert(`삐리릭-! 📡 상담 데이터가 성공적으로 전송되었습니다.\n담당 선생님이 확인 후 빛의 속도로 "응답 신호"를 보내드릴게요!`);
        else alert(`삐리릭-! 📡 상담 데이터가 성공적으로 전송되었습니다.\n담당 선생님이 확인 후 빛의 속도로 "응답 신호"를 보내드릴게요!`);
        setFormData({ studentName: '', phone: '', ageGender: '', region: '', time: '' });
      } else {
        throw new Error('EmailJS SDK가 로드되지 않았습니다.');
      }
    } catch (error) {
      console.error('Email Send Error:', error);
      alert('메일 발송 중 오류가 발생했습니다. 설정값을 확인해주세요.');
    } finally {
      setIsSending(false);
    }
  };

  // 배경 이미지 URL
  const HERO_BG_IMAGE = "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/bg.png?alt=media&token=481896a3-245d-42eb-997a-9889ed95685e";

  return (
    <div className="bg-white text-gray-900 font-sans selection:bg-indigo-100 overflow-x-hidden pb-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fugaz+One&display=swap');
        .brand-font { font-family: 'Fugaz One', sans-serif; }
        .reveal-on-scroll { transition: all 1s cubic-bezier(0.22, 1, 0.36, 1); }
        .bg-grid-light {
          background-image: linear-gradient(#f1f5f9 1.5px, transparent 1.5px), linear-gradient(90deg, #f1f5f9 1.5px, transparent 1.5px);
          background-size: 30px 30px;
        }
        .hero-text-glow {
          text-shadow: 0 0 20px rgba(99, 102, 241, 0.5), 0 0 40px rgba(99, 102, 241, 0.2);
        }
        .divider-tech-pattern {
          background-image: radial-gradient(#6366f1 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .notice-content-scroll::-webkit-scrollbar { width: 4px; }
        .notice-content-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>

      {/* [업데이트] 게시물 클릭 시 확대 보기 및 한 번 더 클릭 시 사라짐 */}
      {expandedNotice && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-300 cursor-pointer"
          onClick={() => setExpandedNotice(null)} // 화면 어디든 한 번 더 클릭하면 사라짐
        >
          <div className="absolute inset-0 bg-gray-950/80 backdrop-blur-xl"></div>
          <div 
            className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-[40px] md:rounded-[60px] overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-500 cursor-default"
            onClick={(e) => e.stopPropagation()} // 내부 영역 클릭 시 닫히는 것 방지 (원할 경우 이 줄을 삭제)
          >
            {/* 상단 닫기 버튼 */}
            <button 
              onClick={() => setExpandedNotice(null)}
              className="absolute top-6 right-6 md:top-10 md:right-10 z-20 w-12 h-12 bg-gray-100 hover:bg-gray-900 hover:text-white transition-all rounded-full flex items-center justify-center"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="flex-1 overflow-y-auto notice-content-scroll">
              {expandedNotice.image && (
                <div className="w-full h-64 md:h-[450px] overflow-hidden">
                  <img src={expandedNotice.image} className="w-full h-full object-cover" alt="" />
                </div>
              )}
              <div className="p-8 md:p-20">
                <div className="mb-10 text-center">
                  <span className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                    {new Date(expandedNotice.createdAt).toLocaleDateString()}
                  </span>
                  <h3 className="text-3xl md:text-5xl font-black italic tracking-tighter brand-font uppercase text-gray-900 leading-tight">
                    {expandedNotice.title}
                  </h3>
                </div>
                <div 
                  className="prose prose-indigo max-w-none text-gray-600 text-lg leading-relaxed prose-img:rounded-[32px] prose-img:shadow-xl prose-img:mx-auto text-center" 
                  dangerouslySetInnerHTML={{ __html: expandedNotice.content }} 
                />
              </div>
            </div>
            
            <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 flex justify-center">
              <button 
                onClick={() => setExpandedNotice(null)}
                className="px-10 py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition shadow-xl"
              >
                CLOSE VIEW
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="home" className="container mx-auto px-4 pt-6 md:pt-10">
        <div className="relative h-[450px] md:h-[600px] rounded-[32px] md:rounded-[48px] overflow-hidden shadow-2xl group reveal-on-scroll opacity-0 translate-y-10">
          <img 
            src={HERO_BG_IMAGE} 
            alt="Hero Background" 
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/95 via-indigo-900/70 to-transparent"></div>
          <div className="absolute inset-0 bg-grid-light opacity-10 mix-blend-overlay"></div>

          <div className="relative h-full flex items-center px-8 md:px-20">
            <div className="max-w-3xl">
              {/* Blinking Badge */}
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/30 backdrop-blur-md text-white border border-white/20 rounded-full text-[10px] md:text-xs font-black mb-8 uppercase tracking-[0.2em] shadow-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-300"></span>
                </span>
                Creative Education Group
              </span>
              
              <h1 className="text-5xl md:text-8xl font-black text-white italic mb-6 md:mb-10 leading-[0.9] tracking-tighter brand-font hero-text-glow">
                Imagination<br/>
                Into <span className="text-indigo-400">Reality.</span>
              </h1>
              
              <p className="text-sm md:text-xl text-indigo-100/80 font-bold leading-relaxed mb-10 md:mb-14 max-w-xl">
                메이커스토리는 단편 일률적인 교육 시스템에서 벗어나 디지털기기와 다양한 도구를 사용한 창의적인 만들기 활동을 돕는 미래형 에듀테크 공간입니다.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('curriculum')} 
                  className="px-8 md:px-12 py-4 md:py-5 bg-white text-gray-900 rounded-2xl md:rounded-3xl font-black text-xs md:text-sm hover:scale-105 transition shadow-2xl uppercase tracking-widest"
                >
                  커리큘럼
                </button>
                <button 
                  onClick={() => scrollToSection('inquiry')} 
                  className="px-8 md:px-12 py-4 md:py-5 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-2xl md:rounded-3xl font-black text-xs md:text-sm hover:bg-white/20 transition uppercase tracking-widest"
                >
                  상담신청
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News & Notice Section */}
      <section className="container mx-auto px-6 py-16 md:py-24 reveal-on-scroll opacity-0 translate-y-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
          <div>
            <h2 className="text-[10px] md:text-sm font-black text-indigo-600 uppercase tracking-[0.5em] mb-4">Update & Announcement</h2>
            <h3 className="text-3xl md:text-5xl font-black italic tracking-tighter brand-font uppercase">News & <span className="text-gray-300">Notice</span></h3>
          </div>
          <div className="flex gap-4">
            <button className="px-6 py-3 bg-gray-50 text-gray-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-900 hover:text-white transition shadow-sm border border-gray-100">
              &lt;&lt; PRE
            </button>
            <button className="px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-900 transition shadow-xl shadow-indigo-100">
              NEXT &gt;&gt;
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {notices.length === 0 ? (
            <div className="col-span-full py-12 bg-gray-50 rounded-[32px] text-center text-gray-300 font-bold italic">
              등록된 최신 소식이 없습니다.
            </div>
          ) : notices.map((notice) => (
            <div 
              key={notice.id} 
              onClick={() => setExpandedNotice(notice)} // 클릭 시 확대 보기 실행
              className="group flex flex-col bg-white border border-gray-100 rounded-[40px] overflow-hidden hover:border-indigo-200 hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
                {notice.image ? (
                  <img 
                    src={notice.image} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                    alt="" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-200 bg-indigo-50/50">
                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth={1.5} />
                    </svg>
                  </div>
                )}
                {notice.isImportant && (
                  <div className="absolute top-4 left-4 px-2.5 py-1 bg-red-600 text-white text-[8px] font-black rounded uppercase tracking-tighter shadow-lg animate-pulse">Urgent</div>
                )}
              </div>
              <div className="p-8 flex flex-col flex-1">
                <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest mb-4">{new Date(notice.createdAt).toLocaleDateString()}</span>
                <h4 className="text-xl font-black text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors line-clamp-1 italic tracking-tight">{notice.title}</h4>
                <div className="text-sm text-gray-500 line-clamp-2 leading-relaxed mb-6 flex-1 opacity-80" dangerouslySetInnerHTML={{ __html: notice.content.substring(0, 100) }} />
                <div className="w-8 h-1 bg-gray-100 group-hover:w-full group-hover:bg-indigo-600 transition-all duration-700 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum, Epilogue, Inquiry sections remain unchanged as requested */}
      <div className="relative h-24 md:h-32 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 divider-tech-pattern opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-24 md:h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0 L0,120 L1200,120 L1200,0 C1100,80 700,100 0,0 Z" fill="#111827"></path>
          </svg>
        </div>
      </div>

      <section id="curriculum" className="pb-24 md:pb-32 bg-gray-900 text-white relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#6366f1 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="container mx-auto px-6 relative z-10 pt-10">
          <div className="text-left md:text-center mb-16 md:mb-24 reveal-on-scroll opacity-0 translate-y-10">
            <h2 className="text-[10px] md:text-sm font-black text-indigo-400 uppercase tracking-[0.5em] mb-6">Learning Process</h2>
            <h3 className="text-4xl md:text-7xl font-black italic tracking-tighter uppercase brand-font leading-tight">Future Tech<br/><span className="text-indigo-500">Mastery</span></h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { step: "01", title: "STARTING CODE", age: "5세 ~ 초1", desc: "스마트블럭을 통해 자연스러운 협응력을 높이고 만들기의 기초를 다지며 즐겁게 논리적인 생각을 이끌어냅니다.", tags: ["언플러그드", "NEMO", "3D-PEN"], img: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/curriculum1.png?alt=media&token=8d7891ae-219f-401f-b708-3fa145a4bbad" },
              { step: "02", title: "MAKING CODE", age: "초2 ~ 초5", desc: "메이킹과 코딩을 통해 생각하는 모든 것들을 구현하고 차별화된 커리큘럼으로 창의적 문제해결을 경험합니다.", tags: ["메이커 코딩", "로보틱스", "GULC"], img: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/curriculum2.png?alt=media&token=74d0b197-6f97-4f33-b3fc-4cb048ac6f9b" },
              { step: "03", title: "CREATING CODE", age: "초6 ~ 중3", desc: "3D 프린터 제작, 모델링, 아두이노를 습득하여 독립적인 창작 활동을 통해 자신만의 포트폴리오를 완성합니다.", tags: ["아두이노", "MODELING", "3DP"], img: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/curriculum3.png?alt=media&token=0de814c9-a5af-4f91-bf88-31063d2cfdd5" },
              { step: "04", title: "ROBOT MAKER", age: "6세 ~ 중3", desc: "로봇을 좋아하는 아이들에게 구조 학습부터 메카트로닉스까지 연령별 체계적인 1:1 맞춤 교육을 제공합니다.", tags: ["커넥트", "테크닉로봇", "코딩"], img: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/curriculum4.png?alt=media&token=5bf4ccfd-e418-4f30-95ab-0efc5258db97" }
            ].map((item, idx) => (
              <div key={idx} className="group relative bg-white/5 border border-white/10 rounded-[40px] md:rounded-[50px] overflow-hidden hover:bg-white/10 transition-all duration-700 hover:-translate-y-4 reveal-on-scroll opacity-0 translate-y-10" style={{ transitionDelay: `${idx * 150}ms` }}>
                <div className="h-40 md:h-48 overflow-hidden relative">
                   <img src={item.img} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-110" alt={item.title} />
                   <div className="absolute inset-0 bg-indigo-900/40 group-hover:bg-transparent transition-colors"></div>
                   <div className="absolute top-4 left-4 md:top-6 md:left-6 w-10 h-10 md:w-12 md:h-12 bg-white rounded-xl md:rounded-2xl flex items-center justify-center text-gray-900 font-black italic text-lg md:text-xl shadow-lg shadow-black/40 brand-font">
                      {item.step}
                   </div>
                </div>
                <div className="p-8 md:p-10 flex flex-col min-h-[300px] md:h-[320px]">
                  <h4 className="text-xl md:text-2xl font-black italic tracking-tighter mb-2 uppercase brand-font group-hover:text-indigo-400 transition-colors">{item.title}</h4>
                  <p className="text-indigo-400 text-[10px] font-black mb-6 uppercase tracking-widest">{item.age}</p>
                  <p className="text-gray-400 text-sm leading-relaxed mb-8 flex-1">{item.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map(tag => (
                      <span key={tag} className="text-[9px] font-bold text-white/30 group-hover:text-white px-2 py-1 bg-white/5 rounded-md uppercase">#{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="relative h-16 pointer-events-none bg-gray-900">
        <svg className="absolute bottom-0 w-full h-16" viewBox="0 0 100 10" preserveAspectRatio="none">
          <polygon points="0,10 5,0 10,10 15,0 20,10 25,0 30,10 35,0 40,10 45,0 50,10 55,0 60,10 65,0 70,10 75,0 80,10 85,0 90,10 95,0 100,10" fill="#f9fafb" />
        </svg>
      </div>

      <section id="epilogue" className="py-24 md:py-32 bg-gray-50 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 md:mb-24 gap-8 reveal-on-scroll opacity-0 translate-y-10">
             <div className="text-left">
                <h2 className="text-[10px] md:text-sm font-black text-indigo-600 uppercase tracking-[0.5em] mb-6">Review & Epilogue</h2>
                <h3 className="text-4xl md:text-7xl font-black italic tracking-tighter uppercase brand-font leading-[0.9]">Real Voice<br/>From Parents</h3>
             </div>
             <p className="text-gray-400 font-bold italic text-base md:text-lg md:text-right max-w-sm">
                메이커스토리가 증명하는 교육의 가치, 실제 학부모님들의 생생한 후기입니다.
             </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              { user: "네모짱 부모님", content: "아이가 지루해할 틈이 없어요! 블럭 크기가 적당해서 연결하기 좋고 자기가 생각한 것들이 실제로 움직이니 너무 신기해합니다.", avatar: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/1.jpg?alt=media&token=bb010b5b-870f-4b4b-b704-a193d1f9f8c6", stat: "창의력 향상 200%" },
              { user: "메이커스토리맘", content: "친절하고 능숙하신 선생님께서 직접 찾아와 가르쳐주시니 정말 편해요. 교구가 많은 편인데도 선생님이 체계적으로 관리해주셔서 만족도가 매우 높습니다.", avatar: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/2.jpg?alt=media&token=e3794df3-4ed4-4c5b-bc75-f2697f4ab637", stat: "방문 교육 대만족" },
              { user: "코딩패밀리", content: "코딩이 정규 교과과정이 되면서 걱정이 많았는데, 여기 수업은 단순 암기가 아니라 메이킹과 연계되어 있어 아이가 지루해하지 않아요.", avatar: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/3.jpg?alt=media&token=971fcbc4-7c9d-4655-b140-a7fe2193c733", stat: "논리력 성장의 기초" }
            ].map((review, idx) => (
              <div key={idx} className="reveal-on-scroll opacity-0 translate-y-10 bg-white rounded-[40px] md:rounded-[50px] p-8 md:p-12 border border-gray-100 flex flex-col hover:shadow-[0_20px_60px_rgba(99,102,241,0.1)] transition-all group" style={{ transitionDelay: `${idx * 200}ms` }}>
                <div className="flex gap-1 mb-6 md:mb-8">
                   {[1,2,3,4,5].map(s => <span key={s} className="text-amber-400 text-base md:text-lg">★</span>)}
                </div>
                <div className="mb-8 md:mb-10 text-gray-600 text-base md:text-lg leading-relaxed flex-1 italic relative">
                  <span className="absolute -top-4 -left-4 text-5xl md:text-6xl text-indigo-100 brand-font opacity-50">"</span>
                  <p className="relative z-10">{review.content}</p>
                </div>
                <div className="flex items-center gap-4 pt-6 md:pt-8 border-t border-gray-50">
                  <img src={review.avatar} className="w-12 h-12 md:w-14 md:h-14 rounded-2xl object-cover shadow-md" alt={review.user} />
                  <div>
                    <span className="font-black italic text-xs md:text-sm uppercase tracking-tighter block">{review.user}</span>
                    <span className="text-[9px] md:text-[10px] font-black text-indigo-500 uppercase tracking-widest">{review.stat}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="relative h-24 pointer-events-none">
        <svg className="absolute inset-0 w-full h-24" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,0 L100,100 L0,100 Z" fill="#4f46e5" />
        </svg>
      </div>

      <section id="inquiry" className="py-24 md:py-32 bg-indigo-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="reveal-on-scroll opacity-0 translate-y-10 text-white text-left">
              <h2 className="text-[10px] font-black text-indigo-200 uppercase tracking-[0.5em] mb-8">Consultation</h2>
              <h3 className="text-4xl sm:text-6xl md:text-8xl font-black italic tracking-tighter uppercase brand-font leading-[1] md:leading-[0.85] mb-12">Talk With<br/>Our Expert</h3>
              <p className="text-lg md:text-xl text-indigo-100/80 mb-12 leading-relaxed">
                아이의 미래를 설계하는 첫 걸음, 지금 바로 상담 신청하세요.
              </p>
              
              <div className="space-y-8 md:space-y-10 text-left max-w-md">
                {[
                  { icon: "📍", label: "Location", value: "대구 수성구 달구벌대로 3125, 2층" },
                  { icon: "📧", label: "Email", value: "makerstoryedu@gmail.com" },
                  { icon: "📞", label: "Call", value: "053-811-8922" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 md:gap-6 items-center group">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-white/10 rounded-[18px] md:rounded-[20px] flex items-center justify-center text-xl md:text-2xl group-hover:bg-white group-hover:text-indigo-600 transition-all">{item.icon}</div>
                    <div>
                       <p className="text-[8px] font-black text-indigo-200 uppercase tracking-widest mb-1">{item.label}</p>
                       <p className="text-base md:text-lg font-bold">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="reveal-on-scroll opacity-0 translate-y-10 bg-white p-8 md:p-16 rounded-[60px] md:rounded-[80px] shadow-3xl border border-white/20">
              <div className="space-y-6 md:space-y-8">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Student Name</label>
                  <input type="text" required value={formData.studentName} onChange={e => setFormData({...formData, studentName: e.target.value})} placeholder="홍 길 동" className="w-full px-6 md:px-8 py-4 md:py-5 bg-gray-50 border-none rounded-2xl md:rounded-3xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm md:text-base" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Phone Number</label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="010-0000-0000" className="w-full px-6 md:px-8 py-4 md:py-5 bg-gray-50 border-none rounded-2xl md:rounded-3xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm md:text-base" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Age & Gender</label>
                    <input type="text" required value={formData.ageGender} onChange={e => setFormData({...formData, ageGender: e.target.value})} placeholder="8세 남" className="w-full px-6 md:px-8 py-4 md:py-5 bg-gray-50 border-none rounded-2xl md:rounded-3xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm md:text-base" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Region</label>
                    <input type="text" required value={formData.region} onChange={e => setFormData({...formData, region: e.target.value})} placeholder="수성구 범어동" className="w-full px-6 md:px-8 py-4 md:py-5 bg-gray-50 border-none rounded-2xl md:rounded-3xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm md:text-base" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Available Time</label>
                  <input type="text" required value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} placeholder="월요일 오후 5시 이후" className="w-full px-6 md:px-8 py-4 md:py-5 bg-gray-50 border-none rounded-2xl md:rounded-3xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm md:text-base" />
                </div>
                <button 
                  type="submit" 
                  disabled={isSending}
                  className="w-full py-5 md:py-6 bg-indigo-600 text-white font-black rounded-2xl md:rounded-3xl hover:bg-gray-900 transition-all shadow-2xl shadow-indigo-100 uppercase tracking-widest text-xs md:text-sm mt-4 transform active:scale-95 brand-font italic disabled:bg-gray-400"
                >
                  {isSending ? 'Sending...' : 'Confirm & Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandStoryPage;
